import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuditQueue } from "./useAuditQueue";
import { byPriorityThenAge } from "../shared/helpers";
import { AuditTask, TASK_STATUS } from "../shared/types";
import * as S from "../shared/styles";
import type {
  AuditQueueTask,
  AuditQueueUseCase,
  AuditQueueResult,
} from "src/store/auditQueueSlice";
import Loader from "@amzn/meridian/loader";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";

const TABS = [
  // keys stay as the URL paths (/vlmAudit/my-tasks ...)
  { key: "my-tasks", label: "Pending" },
  { key: "completed", label: "Completed" },
  // No task expires yet (the backend never returns "expired"); kept for a future expiry rule.
  { key: "expired", label: "Expired" },
] as const;

type TabKey = (typeof TABS)[number]["key"];

const QUEUE_PAGE_SIZE = 10;

const pagerRow: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "12px",
  marginTop: "16px",
};

interface ComparisonProps {
  useCase: AuditQueueUseCase | undefined;
  results: AuditQueueResult[];
  onClose: () => void;
}

const InterRaterComparison: React.FC<ComparisonProps> = ({ useCase, results, onClose }) => {
  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  const fieldSet: Record<string, boolean> = {};
  results.forEach((r: AuditQueueResult) => {
    Object.keys(r.decisions || {}).forEach((k) => {
      fieldSet[k] = true;
    });
  });
  const fields = Object.keys(fieldSet);

  return (
    <div style={S.modalOverlay} onClick={onClose}>
      <div style={S.modalContent} onClick={(e) => e.stopPropagation()}>
        <div style={S.modalHeader}>
          <div>
            <h2 style={S.modalTitle}>Inter-Rater Comparison</h2>
            <p style={S.modalSubtitle}>
              {useCase?.name} · {results.length} reviewers
            </p>
          </div>
          <button style={S.btnSecondary} onClick={onClose}>
            Close
          </button>
        </div>
        <div style={S.modalBody}>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>Field</th>
                {results.map((r) => (
                  <th key={r.id} style={S.th}>
                    {r.auditor_id}
                  </th>
                ))}
                <th style={S.th}>Agreement</th>
              </tr>
            </thead>
            <tbody>
              {fields.map((f) => {
                const decisions = results.map((r) => r.decisions?.[f]?.decision ?? "—");
                const agree = decisions.every((d) => d === decisions[0] && d !== "—");
                return (
                  <tr key={f}>
                    <td style={S.tdField}>{f}</td>
                    {decisions.map((d, i) => (
                      <td key={i} style={S.td}>
                        {String(d).replace(/_/g, " ")}
                      </td>
                    ))}
                    <td style={S.td}>
                      <span style={agree ? S.agreeBadge : S.disagreeBadge}>
                        {agree ? "Agree" : "Disagree"}
                      </span>
                    </td>
                  </tr>
                );
              })}
              {fields.length === 0 && (
                <tr>
                  <td
                    colSpan={results.length + 2}
                    style={{
                      ...S.td,
                      padding: "16px",
                      textAlign: "center",
                      color: "#9ca3af",
                    }}
                  >
                    No field decisions recorded.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ----------------------------  QueueProgress  ----------------------------
// The caller's tasks (for the selected use case) split into pending, completed and
// expired (the same names as the tabs), as one stacked bar.

interface QueueCounts {
  total: number;
  pending: number;
  completed: number;
  expired: number;
}

const countTasks = (tasks: AuditQueueTask[]): QueueCounts => {
  const completed = tasks.filter((t) => t.status === TASK_STATUS.COMPLETED).length;
  const expired = tasks.filter((t) => t.status === TASK_STATUS.EXPIRED).length;
  return { total: tasks.length, completed, expired, pending: tasks.length - completed - expired };
};

const PROGRESS_SEGMENTS = [
  { key: "pending", label: "Pending", color: "#f59e0b" },
  { key: "completed", label: "Completed", color: "#22c55e" },
  { key: "expired", label: "Expired", color: "#9ca3af" },
] as const;

const QueueProgress: React.FC<{ counts: QueueCounts }> = ({ counts }) => {
  const pct = (n: number) => (counts.total ? (n / counts.total) * 100 : 0);
  return (
    <div style={progressCard} aria-label="Audit progress">
      <div style={progressHeader}>
        <span style={{ fontWeight: 600, color: "#1f2937" }}>Progress</span>
        <span style={{ color: "#6b7280" }}>
          {counts.completed} of {counts.total} completed
        </span>
      </div>
      <div style={progressTrack}>
        {PROGRESS_SEGMENTS.map((seg) =>
          counts[seg.key] > 0 ? (
            <div
              key={seg.key}
              title={`${seg.label}: ${counts[seg.key]}`}
              style={{ width: pct(counts[seg.key]) + "%", background: seg.color }}
            />
          ) : null,
        )}
      </div>
      <div style={progressLegend}>
        <span>
          Total <strong>{counts.total}</strong>
        </span>
        {PROGRESS_SEGMENTS.map((seg) => (
          <span key={seg.key} style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
            <span style={{ ...legendDot, background: seg.color }} />
            {seg.label} <strong>{counts[seg.key]}</strong>
          </span>
        ))}
      </div>
    </div>
  );
};

const progressCard: React.CSSProperties = {
  border: "1px solid #e5e7eb",
  borderRadius: "8px",
  padding: "12px 16px",
  marginBottom: "16px",
  background: "white",
  display: "flex",
  flexDirection: "column",
  gap: "8px",
};

const progressHeader: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  fontSize: "1rem",
};

const progressTrack: React.CSSProperties = {
  display: "flex",
  height: "10px",
  borderRadius: "5px",
  overflow: "hidden",
  background: "#f3f4f6",
};

const progressLegend: React.CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: "16px",
  fontSize: "0.95rem",
  color: "#4b5563",
};

const legendDot: React.CSSProperties = {
  width: "10px",
  height: "10px",
  borderRadius: "50%",
  display: "inline-block",
};

// ----------------------------  Main Page  ----------------------------
const AuditQueue: React.FC = () => {
  const { tab: tabParam } = useParams<{ tab?: string }>();
  const tab: TabKey = (tabParam as TabKey) || "my-tasks";
  const navigate = useNavigate();

  const {
    useCases,
    tasks,
    currentUser,
    isLoading,
    isError,
    useCaseById,
    resultsForTask,
    resultForCurrentUser,
  } = useAuditQueue();

  const [ucFilter, setUcFilter] = useState<string>("all");
  const [compareTask, setCompareTask] = useState<AuditQueueTask | null>(null);
  const [search, setSearch] = useState<string>("");
  const [page, setPage] = useState<number>(0);

  const authorizedIds = useMemo(() => useCases.map((u) => u.id), [useCases]);

  const isMine = useCallback(
    (t: AuditQueueTask) => (currentUser.isElevated ? true : t.assigned_to === currentUser.login),
    [currentUser.isElevated, currentUser.login],
  );

  const filtered = useMemo(
    () =>
      tasks
        .filter((t) => authorizedIds.includes(t.use_case_id))
        .filter((t) => (ucFilter === "all" ? true : t.use_case_id === ucFilter))
        .filter((t) => t.id.toLowerCase().includes(search.trim().toLowerCase()))
        .filter((t) => {
          switch (tab) {
            case "my-tasks":
              return (
                isMine(t) && t.status !== TASK_STATUS.COMPLETED && t.status !== TASK_STATUS.EXPIRED
              );
            case "completed":
              return t.status === TASK_STATUS.COMPLETED && isMine(t);
            case "expired":
              return t.status === TASK_STATUS.EXPIRED && isMine(t);
            default:
              return false;
          }
        })
        .sort((a, b) => byPriorityThenAge(a as AuditTask, b as AuditTask)),
    [tasks, authorizedIds, ucFilter, search, tab, currentUser, isMine],
  );

  // Back to the first page whenever the list being paged changes.
  useEffect(() => setPage(0), [tab, ucFilter, search]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / QUEUE_PAGE_SIZE));
  const currentPage = Math.min(page, pageCount - 1);
  const pageTasks = filtered.slice(
    currentPage * QUEUE_PAGE_SIZE,
    (currentPage + 1) * QUEUE_PAGE_SIZE,
  );

  // Progress over every task of the caller's (tab-independent), for the selected use case.
  const progress = useMemo(
    () =>
      countTasks(
        tasks
          .filter((t) => authorizedIds.includes(t.use_case_id))
          .filter((t) => (ucFilter === "all" ? true : t.use_case_id === ucFilter))
          .filter(isMine),
      ),
    [tasks, authorizedIds, ucFilter, isMine],
  );

  const openTask = (task: AuditQueueTask) => {
    navigate(`/vlmAudit/workspace/${task.id}`);
  };

  // Loading state (uses the same Meridian Loader as App.tsx).
  if (isLoading) {
    return (
      <Row width="fill" alignmentHorizontal="center">
        <Column height="60vh" alignmentVertical="center">
          <Loader />
        </Column>
      </Row>
    );
  }

  if (isError) {
    return (
      <div style={S.pageWrapper}>
        <div
          style={{
            ...S.devBanner,
            background: "#fee2e2",
            borderColor: "#ef4444",
            color: "#991b1b",
          }}
        >
          Failed to load audit queue data. Please try refreshing the page.
        </div>
      </div>
    );
  }

  return (
    <div style={S.pageWrapper}>
      {/* Page header */}
      <div style={{ marginBottom: "20px" }}>
        <h1 style={S.pageTitle}>Audit Queue</h1>
        <p style={S.pageSubtitle}>
          Cases are automatically assigned to you. Work through your queue below.
        </p>
      </div>

      {/* Tabs + use-case filter */}
      <div style={S.tabRow}>
        <div style={S.tabGroup}>
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => navigate(`/vlmAudit/${t.key}`)}
              style={tab === t.key ? S.tabActive : S.tabInactive}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div style={S.filterRow}>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search record ID"
            aria-label="Search record ID"
            style={{ ...S.filterSelect, minWidth: "240px" }}
          />
          <span style={S.filterLabel}>Use case:</span>
          <select
            value={ucFilter}
            onChange={(e) => setUcFilter(e.target.value)}
            style={S.filterSelect}
          >
            <option value="all">All</option>
            {useCases.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <QueueProgress counts={progress} />

      {/* Task list */}
      <div style={S.taskList}>
        {filtered.length === 0 && (
          <div style={S.emptyState}>
            {search.trim()
              ? `No tasks match record ID "${search.trim()}".`
              : tab === "my-tasks"
                ? "No cases assigned to you yet — a Program Manager assigns cases weekly."
                : "No tasks in this view."}
          </div>
        )}
        {pageTasks.map((task) => {
          const uc = useCaseById(task.use_case_id);
          const result = resultForCurrentUser(task.id);
          const done = task.status === TASK_STATUS.COMPLETED || task.status === TASK_STATUS.EXPIRED;
          const taskResults = resultsForTask(task.id);
          const submittedResults = taskResults.filter((r) => r.status === "submitted");

          return (
            <div key={task.id} style={S.taskCard}>
              <div>
                <div style={S.taskName}>
                  {uc?.name}
                  {currentUser.isElevated && task.assigned_to && (
                    <span style={S.assignedBadge}>→ {task.assigned_to}</span>
                  )}
                  {(uc?.inter_rater_count ?? 1) > 1 && (
                    <span style={S.interRaterBadge}>
                      {submittedResults.length}/{uc?.inter_rater_count} reviews
                    </span>
                  )}
                </div>
                <div style={S.taskMeta}>
                  Record ID: {task.id} · {task.status}
                  {result?.status === "submitted" && " · you reviewed"}
                  {result?.status === "draft" && " · draft in progress"}
                </div>
              </div>
              <div style={S.taskActions}>
                {done && taskResults.length > 1 && (
                  <button style={S.btnOutline} onClick={() => setCompareTask(task)}>
                    Compare ({taskResults.length})
                  </button>
                )}
                <button style={done ? S.btnSecondary : S.btnPrimary} onClick={() => openTask(task)}>
                  {done ? "View" : result?.status === "draft" ? "Continue" : "Open"}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Pager */}
      {pageCount > 1 && (
        <div style={pagerRow}>
          <button
            style={{ ...S.btnSecondary, opacity: currentPage > 0 ? 1 : 0.5 }}
            disabled={currentPage === 0}
            onClick={() => setPage(currentPage - 1)}
          >
            {"\u2039"} Prev
          </button>
          <span style={{ fontSize: "0.875rem", color: "#6b7280" }}>
            Page {currentPage + 1} of {pageCount} ({filtered.length} tasks)
          </span>
          <button
            style={{ ...S.btnSecondary, opacity: currentPage < pageCount - 1 ? 1 : 0.5 }}
            disabled={currentPage >= pageCount - 1}
            onClick={() => setPage(currentPage + 1)}
          >
            Next {"\u203A"}
          </button>
        </div>
      )}

      {/* Inter-rater comparison modal */}
      {compareTask && (
        <InterRaterComparison
          useCase={useCaseById(compareTask.use_case_id)}
          results={resultsForTask(compareTask.id)}
          onClose={() => setCompareTask(null)}
        />
      )}
    </div>
  );
};

export default AuditQueue;
